import React, { useEffect, useState } from 'react';
import { PencilIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { supabase } from '../../lib/supabase';
import { useFacturasStore } from '../../stores/useFacturasStore';
import { VistaFactura } from '../facturas/VistaFactura';
import { EnviarFactura } from '../facturas/EnviarFactura';
import type { Factura } from '../facturas/types';
import { RegistrarPagoCreditoModal } from './RegistrarPagoCreditoModal';
import { PagoAdjuntosLinks } from './PagoAdjuntosLinks';
import type { CuotaLike } from './creditosUiHelpers';
import { labelEstadoCredito, labelEstadoCuota } from './creditosUiHelpers';

const fmtCop = (n: number) =>
  new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(n);

function fmtFechaPago(iso: string) {
  try {
    return new Date(iso).toLocaleString('es-CO', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export type CreditoDetailDrawerProps = {
  open: boolean;
  onClose: () => void;
  empresaId: string;
  creditoId: string | null;
  onChanged?: () => void;
};

type CreditoHead = {
  id: string;
  cliente_id: string;
  factura_id: string | null;
  tipo: string;
  monto_total: number;
  monto_pagado: number;
  saldo_pendiente: number;
  estado: string;
  fecha_vencimiento: string | null;
};

type CuotaEditState = {
  editingId: string | null;
  fecha_vencimiento: string;
  monto_cuota: string;
  estado: string;
};

type PagoRow = {
  id: string;
  monto: number;
  metodo_pago: string | null;
  fecha_pago: string;
  notas: string | null;
  comprobante_url?: string | null;
  recibo_url?: string | null;
};

export function CreditoDetailDrawer({ open, onClose, empresaId, creditoId, onChanged }: CreditoDetailDrawerProps) {
  const fetchFacturas = useFacturasStore((s) => s.fetchFacturas);
  const updateFactura = useFacturasStore((s) => s.updateFactura);
  const [vistaFactura, setVistaFactura] = useState<Factura | null>(null);
  const [enviarFacturaDoc, setEnviarFacturaDoc] = useState<Factura | null>(null);
  const [loadingVerFactura, setLoadingVerFactura] = useState(false);
  const [head, setHead] = useState<CreditoHead | null>(null);
  const [clienteNombre, setClienteNombre] = useState('—');
  const [cuotas, setCuotas] = useState<CuotaLike[]>([]);
  const [pagos, setPagos] = useState<PagoRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagoOpen, setPagoOpen] = useState(false);
  const [cuotaEdit, setCuotaEdit] = useState<CuotaEditState>({
    editingId: null,
    fecha_vencimiento: '',
    monto_cuota: '',
    estado: 'pendiente',
  });
  const [savingCuota, setSavingCuota] = useState(false);

  useEffect(() => {
    if (!open || !creditoId) {
      setHead(null);
      setCuotas([]);
      setPagos([]);
      setClienteNombre('—');
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data: c0, error: e0 } = await supabase.from('creditos').select('*').eq('id', creditoId).single();
        if (e0) throw e0;
        if (!cancelled) setHead(c0 as CreditoHead);

        const { data: cl } = await supabase
          .from('clientes')
          .select('nombre, apellido')
          .eq('id', (c0 as CreditoHead).cliente_id)
          .maybeSingle();
        if (!cancelled && cl) {
          const n = [cl.nombre, cl.apellido].filter(Boolean).join(' ').trim();
          setClienteNombre(n || '—');
        }

        const { data: cq, error: e1 } = await supabase
          .from('cuotas')
          .select('id, numero_cuota, monto_cuota, monto_pagado, fecha_vencimiento, estado')
          .eq('credito_id', creditoId)
          .eq('empresa_id', empresaId)
          .order('numero_cuota', { ascending: true });
        if (e1) throw e1;
        if (!cancelled) setCuotas((cq as CuotaLike[]) || []);

        const withAdj = await supabase
          .from('pagos_credito')
          .select('id, monto, metodo_pago, fecha_pago, notas, comprobante_url, recibo_url')
          .eq('credito_id', creditoId)
          .eq('empresa_id', empresaId)
          .order('fecha_pago', { ascending: false });
        if (withAdj.error) {
          const { data: pg, error: e2 } = await supabase
            .from('pagos_credito')
            .select('id, monto, metodo_pago, fecha_pago, notas')
            .eq('credito_id', creditoId)
            .eq('empresa_id', empresaId)
            .order('fecha_pago', { ascending: false });
          if (e2) throw e2;
          if (!cancelled) setPagos((pg as PagoRow[]) || []);
        } else if (!cancelled) {
          setPagos((withAdj.data as PagoRow[]) || []);
        }
      } catch {
        if (!cancelled) {
          setHead(null);
          setCuotas([]);
          setPagos([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, creditoId, empresaId]);

  const abrirVerFactura = async () => {
    if (!head?.factura_id) return;
    setLoadingVerFactura(true);
    try {
      await fetchFacturas(empresaId);
      const f = useFacturasStore.getState().facturas.find((x) => x.id === head.factura_id);
      if (!f) {
        window.alert('No se encontró la factura.');
        return;
      }
      setVistaFactura(f);
    } finally {
      setLoadingVerFactura(false);
    }
  };

  const refresh = () => {
    onChanged?.();
    if (!creditoId) return;
    void supabase
      .from('creditos')
      .select('*')
      .eq('id', creditoId)
      .single()
      .then(({ data }) => {
        if (data) setHead(data as CreditoHead);
      });
    void supabase
      .from('cuotas')
      .select('id, numero_cuota, monto_cuota, monto_pagado, fecha_vencimiento, estado')
      .eq('credito_id', creditoId)
      .eq('empresa_id', empresaId)
      .order('numero_cuota', { ascending: true })
      .then(({ data }) => setCuotas((data as CuotaLike[]) || []));
    void supabase
      .from('pagos_credito')
      .select('id, monto, metodo_pago, fecha_pago, notas, comprobante_url, recibo_url')
      .eq('credito_id', creditoId)
      .eq('empresa_id', empresaId)
      .order('fecha_pago', { ascending: false })
      .then(({ data, error }) => {
        if (error) {
          void supabase
            .from('pagos_credito')
            .select('id, monto, metodo_pago, fecha_pago, notas')
            .eq('credito_id', creditoId)
            .eq('empresa_id', empresaId)
            .order('fecha_pago', { ascending: false })
            .then(({ data: d2 }) => setPagos((d2 as PagoRow[]) || []));
          return;
        }
        setPagos((data as PagoRow[]) || []);
      });
  };

  const startEditCuota = (q: CuotaLike) => {
    setCuotaEdit({
      editingId: q.id,
      fecha_vencimiento: q.fecha_vencimiento || '',
      monto_cuota: String(q.monto_cuota ?? ''),
      estado: q.estado || 'pendiente',
    });
  };

  const cancelEditCuota = () => {
    setCuotaEdit({ editingId: null, fecha_vencimiento: '', monto_cuota: '', estado: 'pendiente' });
  };

  const saveEditCuota = async () => {
    if (!cuotaEdit.editingId || !creditoId) return;
    const mCuota = Math.round(Number(String(cuotaEdit.monto_cuota).replace(/[^\d.-]/g, '')) || 0);
    const q = cuotas.find((c) => c.id === cuotaEdit.editingId);
    const mp = q ? Math.round(Number(q.monto_pagado) || 0) : 0;
    if (mCuota < mp) {
      alert('El monto de la cuota no puede ser menor a lo ya pagado.');
      return;
    }
    setSavingCuota(true);
    try {
      const { error } = await supabase
        .from('cuotas')
        .update({
          fecha_vencimiento: cuotaEdit.fecha_vencimiento || null,
          monto_cuota: mCuota,
          estado: cuotaEdit.estado,
        })
        .eq('id', cuotaEdit.editingId)
        .eq('credito_id', creditoId)
        .eq('empresa_id', empresaId);
      if (error) throw error;
      cancelEditCuota();
      refresh();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'No se pudo guardar la cuota.');
    } finally {
      setSavingCuota(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-[100] bg-black/30" aria-hidden onClick={onClose} />
      <div className="fixed inset-y-0 right-0 z-[101] flex w-full max-w-md flex-col border-l border-gray-200 bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <h2 className="text-base font-semibold text-gray-900">Detalle del crédito</h2>
          <button type="button" className="rounded-full p-1 text-gray-500 hover:bg-gray-100" onClick={onClose} aria-label="Cerrar">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {loading || !head ? (
            <p className="text-sm text-gray-500">{loading ? 'Cargando…' : 'No se encontró el crédito.'}</p>
          ) : (
            <div className="space-y-4">
              <div className="rounded-lg border border-gray-100 bg-slate-50 p-3 text-sm">
                <p className="font-medium text-gray-900">{clienteNombre}</p>
                <p className="mt-1 text-gray-600">
                  Total {fmtCop(Number(head.monto_total) || 0)} · Saldo {fmtCop(Number(head.saldo_pendiente) || 0)}
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  Tipo: {head.tipo === 'simple' ? 'Simple' : 'Plan de pagos'} · {labelEstadoCredito(head.estado)}
                </p>
              </div>
              {head.factura_id ? (
                <button
                  type="button"
                  disabled={loadingVerFactura}
                  className="mt-1 w-full rounded-lg border border-gray-200 bg-gray-100 px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-200 disabled:opacity-60"
                  onClick={() => void abrirVerFactura()}
                >
                  {loadingVerFactura ? 'Abriendo…' : 'Ver factura'}
                </button>
              ) : null}
              <button
                type="button"
                className="w-full rounded-lg py-2.5 text-sm font-medium text-white"
                style={{ backgroundColor: '#1A56DB' }}
                onClick={() => setPagoOpen(true)}
              >
                Registrar pago
              </button>
              <div>
                <h3 className="mb-2 text-xs font-semibold uppercase text-gray-500">Cuotas</h3>
                <div className="overflow-hidden rounded-lg border border-gray-200">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-gray-50 text-[10px] uppercase text-gray-500">
                      <tr>
                        <th className="px-2 py-2">#</th>
                        <th className="px-2 py-2">Vence</th>
                        <th className="px-2 py-2 text-right">Cuota</th>
                        <th className="px-2 py-2 text-right">Pagado</th>
                        <th className="px-2 py-2">Estado</th>
                        <th className="px-1 py-2 w-8" />
                      </tr>
                    </thead>
                    <tbody>
                      {cuotas.map((q) =>
                        cuotaEdit.editingId === q.id ? (
                          <tr key={q.id} className="border-t border-gray-100 bg-blue-50/50">
                            <td className="px-2 py-1.5 align-top">{q.numero_cuota}</td>
                            <td className="px-1 py-1 align-top">
                              <input
                                type="date"
                                value={cuotaEdit.fecha_vencimiento}
                                onChange={(e) => setCuotaEdit((s) => ({ ...s, fecha_vencimiento: e.target.value }))}
                                className="w-full min-w-[7.5rem] rounded border border-gray-300 px-1 py-0.5 text-[11px]"
                              />
                            </td>
                            <td className="px-1 py-1 align-top">
                              <input
                                type="number"
                                value={cuotaEdit.monto_cuota}
                                onChange={(e) => setCuotaEdit((s) => ({ ...s, monto_cuota: e.target.value }))}
                                className="w-full max-w-[6rem] rounded border border-gray-300 px-1 py-0.5 text-right text-[11px]"
                              />
                            </td>
                            <td className="px-2 py-1.5 text-right align-top text-gray-700">
                              {fmtCop(Number(q.monto_pagado) || 0)}
                            </td>
                            <td className="px-1 py-1 align-top">
                              <select
                                value={cuotaEdit.estado}
                                onChange={(e) => setCuotaEdit((s) => ({ ...s, estado: e.target.value }))}
                                className="w-full min-w-[5.5rem] rounded border border-gray-300 px-0.5 py-0.5 text-[11px]"
                              >
                                <option value="pendiente">Pendiente</option>
                                <option value="pagada">Pagada</option>
                                <option value="vencida">Vencida</option>
                                <option value="parcial">Parcial</option>
                              </select>
                            </td>
                            <td className="px-1 py-1 align-top text-right">
                              <div className="flex items-center justify-end gap-0.5">
                                <button
                                  type="button"
                                  className="rounded p-1 text-green-600 hover:bg-green-50"
                                  title="Guardar"
                                  disabled={savingCuota}
                                  onClick={() => void saveEditCuota()}
                                >
                                  ✓
                                </button>
                                <button
                                  type="button"
                                  className="rounded p-1 text-gray-500 hover:bg-gray-100"
                                  title="Cancelar"
                                  onClick={cancelEditCuota}
                                >
                                  ✕
                                </button>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          <tr key={q.id} className="border-t border-gray-100">
                            <td className="px-2 py-1.5">{q.numero_cuota}</td>
                            <td className="px-2 py-1.5">
                              {q.fecha_vencimiento
                                ? new Date(`${q.fecha_vencimiento}T12:00:00`).toLocaleDateString('es-CO', {
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric',
                                  })
                                : '—'}
                            </td>
                            <td className="px-2 py-1.5 text-right">{fmtCop(Number(q.monto_cuota) || 0)}</td>
                            <td className="px-2 py-1.5 text-right">{fmtCop(Number(q.monto_pagado) || 0)}</td>
                            <td className="px-2 py-1.5">
                              <span
                                className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-medium ${
                                  q.estado === 'pagada'
                                    ? 'bg-green-100 text-green-800'
                                    : q.estado === 'vencida'
                                      ? 'bg-red-100 text-red-800'
                                      : q.estado === 'parcial'
                                        ? 'bg-amber-100 text-amber-900'
                                        : 'bg-gray-100 text-gray-700'
                                }`}
                              >
                                {labelEstadoCuota(q.estado)}
                              </span>
                            </td>
                            <td className="px-1 py-1 text-right">
                              <button
                                type="button"
                                className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                                title="Editar cuota"
                                onClick={() => startEditCuota(q)}
                              >
                                <PencilIcon className="h-3.5 w-3.5" />
                              </button>
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <div>
                <h3 className="mb-2 text-xs font-semibold uppercase text-gray-500">Historial de pagos</h3>
                {pagos.length === 0 ? (
                  <p className="text-sm text-gray-500">Sin pagos registrados.</p>
                ) : (
                  <ul className="space-y-2">
                    {pagos.map((p) => (
                      <li key={p.id} className="rounded-lg border border-gray-100 px-3 py-2 text-sm">
                        <div className="flex justify-between gap-2">
                          <span className="font-semibold text-gray-900">{fmtCop(Number(p.monto) || 0)}</span>
                          <span className="text-xs text-gray-500">{fmtFechaPago(p.fecha_pago)}</span>
                        </div>
                        <p className="text-xs text-gray-600">{p.metodo_pago || '—'}</p>
                        {p.notas ? <p className="mt-1 text-xs text-gray-500">{p.notas}</p> : null}
                        <PagoAdjuntosLinks comprobanteUrl={p.comprobante_url} reciboUrl={p.recibo_url} />
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <RegistrarPagoCreditoModal
        open={pagoOpen}
        onClose={() => setPagoOpen(false)}
        empresaId={empresaId}
        creditoId={creditoId}
        cuotasIniciales={cuotas}
        onSuccess={() => {
          refresh();
        }}
      />

      {vistaFactura && (
        <VistaFactura
          factura={vistaFactura}
          onClose={() => setVistaFactura(null)}
          onEnviar={() => {
            const f = vistaFactura;
            setVistaFactura(null);
            if (f) setEnviarFacturaDoc(f);
          }}
        />
      )}

      {enviarFacturaDoc && (
        <EnviarFactura
          factura={enviarFacturaDoc}
          onClose={() => setEnviarFacturaDoc(null)}
          onEnviar={async (canal) => {
            const base = enviarFacturaDoc;
            if (!base) return;
            const facturaActualizada: Factura = {
              ...base,
              estado: 'ENVIADA',
              mensajesEnviados: [
                ...(base.mensajesEnviados || []),
                {
                  id: Date.now().toString(),
                  fecha: new Date(),
                  canal,
                  estado: 'ENVIADO',
                },
              ],
              ultimaActividad: new Date(),
            };
            await updateFactura(base.id, facturaActualizada);
            setEnviarFacturaDoc(null);
            await fetchFacturas(empresaId);
            onChanged?.();
          }}
        />
      )}
    </>
  );
}
